import { useEffect } from "react";
import type { AiBudgetSpentOut } from "../../api/types";
import { COACH_COPY } from "../../copy/coach";
import { fill } from "../../copy/firstRun";

/**
 * A spent AI budget, as the coach page shows it (#394, C27; owner rulings
 * CAP-O16 and CAP-O19).
 *
 * `POST /api/coach/chat` answers 429 with `reason: "ai_budget_spent"` when the
 * user's AI allowance for the period is used up. The server sends the window
 * and the instant it renews and no sentence: the coach has no model call to
 * spend, so the words are chrome in `COACH_COPY`, English whatever the coach
 * speaks, with `{when}` filled here in the device's own zone.
 *
 * Two things the page does with it, both from this one value: a coach message
 * in the transcript (`bubble`) and a strip above the composer for as long as
 * it lasts (`strip`), with send locked until the instant. Nothing here is
 * stored: a reload forgets it and the first send after it learns it again,
 * which costs one more message and no stale state.
 */

/** What a client reads from the 429. `period` is an open vocabulary on purpose. */
export interface BudgetSpent {
  /**
   * The window that binds: "day", "month", or "cap" (the deployment's own daily limit, #395,
   * which is the UTC day and is read as the day); read any other as "a period".
   */
  period: string;
  /** When a message would pass again, for the words; null when it never does (a budget of zero). */
  renewsAt: Date | null;
  /**
   * When send unlocks, on THIS device's clock; null with `renewsAt`. Taken from the
   * relative `Retry-After` when the response has one, so a device whose clock is
   * minutes out neither unlocks at once (and is refused again, one more message
   * each time) nor stays locked past the instant. Else the instant itself.
   */
  unlockAt: Date | null;
}

/**
 * The refusal in a 429's body, or null when this 429 is something else.
 *
 * Only an explicit `renews_at: null` means "never". An instant that is missing or
 * cannot be read, with no `Retry-After` to stand in for it, is not a refusal this
 * client can act on: it is read as null, so the page shows the failure as it shows
 * any other, and never locks send on a body it did not understand.
 */
export function readBudgetSpent(
  status: number,
  bodyText: string,
  retryAfter: string | null = null,
  now: Date = new Date(),
): BudgetSpent | null {
  // The coach's own rate limit is a 429 too: `{"detail": "<string>"}`, no `reason`.
  if (status !== 429) return null;
  let body: Partial<AiBudgetSpentOut> | null;
  try {
    body = JSON.parse(bodyText) as Partial<AiBudgetSpentOut> | null;
  } catch {
    return null;
  }
  if (body === null || typeof body !== "object" || body.reason !== "ai_budget_spent") {
    return null;
  }
  const period = typeof body.period === "string" ? body.period : "";
  if (body.renews_at === null) return { period, renewsAt: null, unlockAt: null };
  const parsed = typeof body.renews_at === "string" ? new Date(body.renews_at) : null;
  const instant = parsed !== null && !Number.isNaN(parsed.getTime()) ? parsed : null;
  const seconds = retryAfter !== null && /^\d+$/.test(retryAfter.trim()) ? Number(retryAfter) : null;
  const relative = seconds !== null && seconds > 0 ? new Date(now.getTime() + seconds * 1000) : null;
  if (instant === null && relative === null) return null;
  return {
    period,
    // The words need an instant; with only a delay, now plus the delay is the instant.
    renewsAt: instant ?? relative,
    unlockAt: relative ?? instant,
  };
}

const DAY_MS = 86_400_000;

/** The device's calendar day of `d`, as a day number, so two can be subtracted. */
function calendarDay(d: Date, timeZone: string | undefined): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(d);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(n("year"), n("month") - 1, n("day")) / DAY_MS;
}

/**
 * `{when}`: the renewal instant in words, in the device's zone (or `timeZone`).
 * "today at 2:03 PM", "tomorrow at 2:03 PM", "on Oct 31 at 7:00 PM".
 *
 * The instant is formatted, never a guessed date: a month renews at 00:00 UTC,
 * which is the previous evening west of Greenwich, so "the 1st" would be wrong
 * there. English, like the rest of the page (CLAUDE.md, *Language*), with the
 * device's own time zone. The space before AM or PM is a no-break space so a
 * time never wraps in the middle.
 *
 * **Rounded up to the next whole minute.** The words name minutes and the lock
 * lifts to the second: shown as 1:27 for 1:27:40, a user who tries at 1:27:10
 * would find send still locked. A moment never earlier than the real one, the
 * same rule the server applies to the second.
 */
export function whenPhrase(instant: Date, now: Date, timeZone?: string): string {
  const renewsAt = new Date(Math.ceil(instant.getTime() / 60_000) * 60_000);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  })
    .format(renewsAt)
    .replace(/[\u202f\u0020]/g, "\u00a0");
  const days = calendarDay(renewsAt, timeZone) - calendarDay(now, timeZone);
  // The words around the time are the deck's (CAP-O33); `time` and `date` are this
  // client's own formatter's output.
  if (days <= 0) return fill(COACH_COPY.budgetWhenToday, { time });
  if (days === 1) return fill(COACH_COPY.budgetWhenTomorrow, { time });
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
  }).format(renewsAt);
  return fill(COACH_COPY.budgetWhenLater, { date, time });
}

/** The two texts a refusal shows. */
export interface BudgetSpentText {
  /** The coach's message in the transcript. */
  bubble: string;
  /** The strip above the composer. */
  strip: string;
}

/**
 * The words for `spent`. A period this client does not know is read as the
 * day's: the instant, which is what the user acts on, is exact either way.
 * With no instant (a budget of zero) there is no `{when}`: the bubble is
 * `budgetSpentOff` and the strip is its own, shorter `budgetStripOff` (CAP-O25).
 */
export function budgetSpentText(
  spent: BudgetSpent,
  now: Date = new Date(),
  timeZone?: string,
): BudgetSpentText {
  if (spent.renewsAt === null) {
    return { bubble: COACH_COPY.budgetSpentOff, strip: COACH_COPY.budgetStripOff };
  }
  const when = whenPhrase(spent.renewsAt, now, timeZone);
  const template =
    spent.period === "month" ? COACH_COPY.budgetSpentMonth : COACH_COPY.budgetSpentDay;
  return {
    bubble: fill(template, { when }),
    strip: fill(COACH_COPY.budgetStrip, { when }),
  };
}

/** `setTimeout` keeps a 32-bit delay: past about 24.8 days it fires at once. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * Calls `onRenewed` at the instant `unlockAt` (`BudgetSpent.unlockAt`: the lock's instant on
 * this device's clock, not the displayed one), once. Re-armed in steps below
 * `setTimeout`'s limit (a month's renewal can be 31 days away), and checked
 * again when the tab becomes visible, because a hidden tab's timers are held
 * back and the strip must not outlive the instant it announces. Does nothing
 * for no instant: a budget that never renews is cleared by a reload.
 */
export function useRenewalTimer(unlockAt: Date | null, onRenewed: () => void): void {
  const at = unlockAt?.getTime() ?? null;
  useEffect(() => {
    if (at === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      const remaining = at - Date.now();
      if (remaining <= 0) {
        onRenewed();
        return;
      }
      timer = setTimeout(tick, Math.min(remaining, MAX_TIMEOUT_MS));
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(timer);
      tick();
    };
    timer = setTimeout(tick, Math.max(0, Math.min(at - Date.now(), MAX_TIMEOUT_MS)));
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [at, onRenewed]);
}
